import { Box, FormHelperText } from '@mui/material';
import { FormNumberField, FormSelect, FormSelectOption, FormTextField } from 'components/forms';
import { addDeviationFormRowStyle } from 'pages/addDeviations/AddDeviationForm';
import { EditDeviationEffectiveDate } from 'pages/addDeviations/effectiveDate';
import { EditDeviationExpirationDate } from 'pages/addDeviations/expirationDate';
import { EditDeviationOneTimeOnly } from 'pages/addDeviations/oneTimeOnly';
import { useEffect } from 'react';
import { Control, DeepMap, FieldError, RegisterOptions, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { COMPETITOR_OPTIONS } from 'shared/constants';
import { useCompetitorProductOptions, useGetEndUserDropdown } from 'shared/hooks';
import { useGetEndUserPricingGroupDropdown } from 'shared/hooks/useGetEndUserPricingGroupDropdown';
import { determineEnduserDisplay } from 'shared/utility';
import { isPositiveOrZero } from 'shared/utility/dataGrid';
import {
    CompetitorProductDropdown,
    CurrentPricesData,
    EndUserDropdown,
    EndUserPricingGroupDropdown,
} from 'types/backend';
import { TAddedDeviation } from 'types/forms';
import { AccountNumberParams } from './managePricingResolver';

interface EditDeviationFormProps {
    control: Control<TAddedDeviation>;
    errors: DeepMap<TAddedDeviation, FieldError> | undefined;
    watch?: UseFormWatch<TAddedDeviation>;
    setValue?: UseFormSetValue<TAddedDeviation>;
    row?: CurrentPricesData;
}

export type IEditDeviationFormChildrenProps = Pick<EditDeviationFormProps, 'control' | 'watch' | 'setValue'> & {
    rules?: Omit<RegisterOptions<TAddedDeviation>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
    fieldError?: FieldError;
    errors?: DeepMap<TAddedDeviation, FieldError>;
};

export function EditDeviationForm({ control, errors, watch, setValue, row }: EditDeviationFormProps) {
    const params = useParams<AccountNumberParams>();
    const endUserDropdownResponse = useGetEndUserDropdown(params.accountNumber);
    const isEndUserDropdownLoading = endUserDropdownResponse.isLoading || endUserDropdownResponse.isFetching;

    const endUserDropdownOptions: FormSelectOption<EndUserDropdown>[] =
        endUserDropdownResponse.data?.rows?.map((endUser) => {
            return {
                value: endUser.endusernumber,
                displayText: determineEnduserDisplay(
                    endUser.city,
                    endUser.state,
                    endUser.street,
                    endUser.endusername,
                    endUser.endusernumber
                ),
                original: endUser,
            };
        }) || [];

    const enduseraccountnumber = watch ? watch(`modelCurrentPrice.enduseraccountnumber`) : '';

    useEffect(() => {
        if (
            setValue &&
            !!endUserDropdownResponse.data?.rows?.length &&
            endUserDropdownResponse.data.rows.length > 0 &&
            !enduseraccountnumber
        ) {
            const matchingEndUser = endUserDropdownResponse.data.rows.find(
                (option) => option.endusernumber === row?.endUserNumber
            );
            if (matchingEndUser) {
                setValue('modelCurrentPrice.enduseraccountnumber', matchingEndUser.endusernumber);
            }
        }
    }, [setValue, endUserDropdownResponse.data?.rows, enduseraccountnumber, row]);

    const endUserPricingGroupDropdownResponse = useGetEndUserPricingGroupDropdown(params.accountNumber);
    const isEndUserPricingGroupDropdownLoading =
        endUserPricingGroupDropdownResponse.isLoading || endUserPricingGroupDropdownResponse.isFetching;
    const endUserPricingGroupDropdownOptions: FormSelectOption<EndUserPricingGroupDropdown>[] =
        endUserPricingGroupDropdownResponse.data?.rows
            ?.map((row) => {
                return {
                    value: row.endUserPricingGroup,
                    displayText: row.endUserPricingGroup,
                    original: row,
                };
            })
            .sort((a, b) => a.displayText.localeCompare(b.displayText)) || [];

    const competitorOption = watch ? watch(`modelCurrentPrice.competitor`) : '';
    const {
        data: competitorProductDropdownResponse,
        isLoading: competitorProductsIsLoading,
        isFetching: competitorProductsIsFetching,
    } = useCompetitorProductOptions(competitorOption);

    const competitorProductDropdownOptions: FormSelectOption<CompetitorProductDropdown>[] =
        competitorProductDropdownResponse?.map((competitorProduct) => {
            return {
                key: competitorProduct.competitorProductsId,
                value: competitorProduct.competitorProduct,
                displayText: competitorProduct.competitorProduct,
                original: competitorProduct,
            };
        }) || [];

    const isCompetitorProductDropdownLoading = competitorProductsIsLoading || competitorProductsIsFetching;

    const deviationType = watch ? watch(`deviationDetails.deviationType`) : '';

    const competitorRequiredRule = { required: '' };
    const competitorProductRequiredRule = { required: '' };
    const competitorProductPriceRequiredRule = { required: '' };
    const deviationPriceRequiredRule = { required: 'New deviated price is required', validate: isPositiveOrZero };
    const estAnnualPriceRequiredRule = { required: '' };
    const deviationNotesRequiredRule = { required: 'Deviation reason notes are required' };

    return (
        <>
            <Box sx={addDeviationFormRowStyle}>
                <Box sx={{ gridColumn: 1 }}>
                    <FormSelect
                        name={`deviationDetails.deviationType`}
                        label='Deviation Type'
                        control={control}
                        rules={{
                            required: 'Deviation Type is required',
                            validate: {
                                noAvailablePricingGroups: (value) =>
                                    value === 'Pricing Group' &&
                                    !endUserPricingGroupDropdownOptions.length &&
                                    !isEndUserPricingGroupDropdownLoading
                                        ? 'No available pricing groups'
                                        : undefined,
                            },
                        }}
                        options={[
                            { value: 'End User', displayText: 'End User' },
                            { value: 'Pricing Group', displayText: 'Pricing Group' },
                        ]}
                        fieldError={errors?.['deviationDetails']?.['deviationType']}
                        disabled
                    />
                    <FormHelperText error>
                        {errors?.['deviationDetails']?.['deviationType']?.['message']}
                    </FormHelperText>
                </Box>
                {deviationType === 'Pricing Group' ? (
                    <Box sx={{ gridColumn: 2 }}>
                        <FormSelect
                            name={`modelCurrentPrice.enduserpricinggroup`}
                            label='Pricing Group'
                            control={control}
                            options={endUserPricingGroupDropdownOptions}
                            isLoading={isEndUserPricingGroupDropdownLoading}
                            disabled={true}
                            rules={{ required: 'Pricing Group is required' }}
                            watch={watch}
                            fieldError={errors?.['modelCurrentPrice']?.['enduserpricinggroup']}
                        />
                        <FormHelperText error>
                            {errors?.['modelCurrentPrice']?.['enduserpricinggroup']?.['message'] ||
                            !endUserPricingGroupDropdownOptions.length
                                ? 'No available pricing groups'
                                : undefined}
                        </FormHelperText>
                    </Box>
                ) : (
                    <Box sx={{ gridColumn: 2 }}>
                        <FormSelect
                            name={`modelCurrentPrice.enduseraccountnumber`}
                            label='End User'
                            control={control}
                            options={endUserDropdownOptions}
                            isLoading={isEndUserDropdownLoading}
                            disabled={true}
                            rules={{ required: 'End User or Group is required' }}
                            watch={watch}
                            sideEffectHandler={() => {
                                const watchedAccountNumber = watch
                                    ? watch(`modelCurrentPrice.enduseraccountnumber`)
                                    : '';
                                const endUserDropdownItem = endUserDropdownOptions.find((endUserDropdownOption) => {
                                    return endUserDropdownOption.value === watchedAccountNumber;
                                });
                                if (endUserDropdownItem?.original && setValue) {
                                    setValue(`endUserDisplay`, endUserDropdownItem.original.endusername);
                                    setValue(`street`, endUserDropdownItem.original.street);
                                    setValue(`city`, endUserDropdownItem.original.city);
                                    setValue(`state`, endUserDropdownItem.original.state);
                                }
                            }}
                            fieldError={errors?.['modelCurrentPrice']?.['enduseraccountnumber']}
                        />
                        <FormHelperText error>
                            {errors?.['modelCurrentPrice']?.['enduseraccountnumber']?.['message']}
                        </FormHelperText>
                    </Box>
                )}
                <EditDeviationEffectiveDate control={control} watch={watch} setValue={setValue} errors={errors} />
                <EditDeviationExpirationDate control={control} errors={errors} />
                <EditDeviationOneTimeOnly control={control} watch={watch} setValue={setValue} />
            </Box>
            <Box sx={addDeviationFormRowStyle}>
                <Box sx={{ gridColumn: 1 }}>
                    <FormNumberField
                        name={`modelCurrentPrice.price`}
                        label='New Deviated Price'
                        control={control}
                        rules={deviationPriceRequiredRule}
                        fieldError={errors?.['modelCurrentPrice']?.['price']}
                        type='money'
                    />
                </Box>
                <Box sx={{ gridColumn: 2 }}>
                    <FormSelect
                        name={`deviationDetails.deviationReason`}
                        label='Deviation Reason'
                        control={control}
                        rules={{ required: 'Deviation Reason is required' }}
                        options={[
                            { value: 'Volume Growth', displayText: 'Volume Growth' },
                            { value: 'Competition', displayText: 'Competition' },
                            { value: 'Other', displayText: 'Other' },
                        ]}
                        fieldError={errors?.['deviationDetails']?.['deviationReason']}
                    />
                    <FormHelperText error>
                        {errors?.['deviationDetails']?.['deviationReason']?.['message']}
                    </FormHelperText>
                </Box>
                <Box sx={{ gridColumn: 3 }}>
                    <FormNumberField
                        name={`modelCurrentPrice.estmonthlyrevenue`}
                        label='Est. Annual Volume'
                        control={control}
                        rules={estAnnualPriceRequiredRule}
                        fieldError={errors?.['modelCurrentPrice']?.['estmonthlyrevenue']}
                        type='number'
                    />
                </Box>
                <Box sx={{ gridColumn: 4 }}>
                    <FormSelect
                        name={`modelCurrentPrice.competitor`}
                        label='Competitor'
                        control={control}
                        rules={competitorRequiredRule}
                        options={COMPETITOR_OPTIONS}
                        fieldError={errors?.['modelCurrentPrice']?.['competitor']}
                    />
                    <FormHelperText error>{errors?.['modelCurrentPrice']?.['competitor']?.['message']}</FormHelperText>
                </Box>
                <Box sx={{ gridColumn: 5 }}>
                    <FormNumberField
                        name={`modelCurrentPrice.competitorprice`}
                        label='Competitor Price'
                        control={control}
                        rules={competitorProductPriceRequiredRule}
                        fieldError={errors?.['modelCurrentPrice']?.['competitorprice']}
                        type='money'
                    />
                </Box>
                <Box sx={{ gridColumn: 1 }}>
                    <FormSelect
                        name={`competitorProduct`}
                        label='Competitor Product'
                        control={control}
                        options={competitorProductDropdownOptions}
                        isLoading={isCompetitorProductDropdownLoading}
                        rules={competitorProductRequiredRule}
                        watch={watch}
                        fieldError={errors?.['competitorProduct']}
                    />
                    <FormHelperText error>{errors?.['competitorProduct']?.message}</FormHelperText>
                </Box>
                <Box sx={{ gridColumn: 2 }}>
                    <FormTextField
                        name={`deviationReasonNotes`}
                        label={'Deviation Reason Notes'}
                        control={control}
                        rules={deviationNotesRequiredRule}
                        fieldError={errors?.['deviationReasonNotes']}
                    />
                </Box>
            </Box>
        </>
    );
}
